import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import type { Role } from 'datocms-plugin-sdk';

export function useFlagsCombination(currentRole: Role) {
  const isDatoUser =
    currentRole?.attributes && currentRole.attributes.name !== 'Unknown';
  const ldClient = useLDClient();
  const [initialized, setInitialized] = useState(false);

  const ldContext = ldClient?.getContext();
  useEffect(() => {
    if (ldContext && !ldContext.anonymous && !initialized) {
      console.log('useFlagsCombination initialized', currentRole, ldContext);
      setInitialized(true);
    }
  }, [ldContext]);

  const {
    contentStudioEnableSharing,
    contentStudioEnableClipProduceBtn,
    contentStudioEnableClipPublishBtn,
    contentStudioEnableEmailRegenerationBtn,
    contentStudioEnableShareableImageRegeneration,
    contentStudioEnableBlogRegeneration,
    contentStudioEnableTalkingPointsRegenerationBtn,
    contentStudioEnableEmailShareBtn,
    contentStudioEnableBlogShareBtn,
    contentStudioEnableShareableImageShareBtn,
    contentStudioEnableClipShareBtn,
    contentStudioEnableBlogSaveBtn,
    contentStudioEnableEmailSaveBtn,
    contentStudioEnableShareableImageSaveBtn,
    contentStudioEnableTalkingPointsSaveBtn,
    contentStudioEnableClipEdit,
    contentStudioEnableAiContentLanguageSelection,
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    isEnterprisePlatform,
    enableCreatorStudio,
    hideStoryDashboardNav,
    enableImportXml,
    enableAiStoryProducer,
    enableFixTranscriptButton,
    storyManagerEnableAddStories,
    // renderUsingChunks,
    enableReframingModeIn169,
    enableLargeFileSizeUpload,
    // enableHeadlessEditor,
    // enableHeadlessEditorExperimental,
    // enableHeadlessEditorWave3,
    // enableHeadlessEditorWave4,
    enableNoLimitFileSizeUpload,
    enableStackedLayouts,
    enableTimelineSnapGuide,
    enableTimelineSnapping,
    enableExperimentalTimelineFrameThumbnails,
    enableTranscriptEditor,
    enableMultiFileConcat,
    removeTinyFragmentsFromSource,
    enableSpeakersIdentification,
    enableTranscriptProviderSelector,
    enableClipThemeGeneration,
    enableTranscriptSegmentArchitecture,
    storyManagerEnableAwsSdkUploads,
    enableUrlUpload,
    enableZoomConnection,
    enableTranscriptDualWrite,
    enableTranscriptMigrationSizeLimit,
    storyManagerEnableUnifiedUploadProgress,
    enableGenerateAiPhoto,

    // New S3 resources upload paths
    // Enables dual write to S3 using signed urls and API upload
    enableVideoResourcesS3DualWrite,
    // Enables dual write transcript snapshots to S3 and Dato API
    enableTranscriptSnapshotResourceDualWrite,
    // Enables upload of transcript snapshots to S3 with VideoResourcesProvider
    enableTranscriptSnapshotResourceUpload,
    // Enables upload ofvideo source to S3 using signed urls
    enableVideoResourcesS3Upload,
    enableParallelClipSave,
    enableNewKaraokeArchitecture,
    enableKaraokeDualWrite,
  } = useFlags();

  const enableB2B = isEnterprisePlatform || isDatoUser;

  function resolveB2BFlag(flag: 'on' | 'off' | 'unset') {
    if (isDatoUser) return true;
    if (flag === 'off') return false;
    return flag === 'on' || enableB2B;
  }

  function resolveAdminFlag(...flags: Array<'on' | 'off' | 'unset'>) {
    return (
      isDatoUser ||
      (flags.some(flag => flag === 'on') && !flags.some(flag => flag === 'off'))
    );
  }

  return {
    initialized,
    enableSharing: resolveB2BFlag(contentStudioEnableSharing),
    contentStudioEnableClipProducer: resolveB2BFlag(
      contentStudioEnableClipProduceBtn,
    ),
    contentStudioEnableClipPublish: resolveB2BFlag(
      contentStudioEnableClipPublishBtn,
    ),
    contentStudioEnableEmailShare: resolveB2BFlag(
      contentStudioEnableEmailShareBtn,
    ),
    contentStudioEnableBlogShare: resolveB2BFlag(
      contentStudioEnableBlogShareBtn,
    ),
    contentStudioEnableImageShare: resolveB2BFlag(
      contentStudioEnableShareableImageShareBtn,
    ),
    contentStudioEnableClipShare: resolveB2BFlag(
      contentStudioEnableClipShareBtn,
    ),
    contentStudioEnableBlogSave: resolveB2BFlag(contentStudioEnableBlogSaveBtn),
    contentStudioEnableEmailSave: resolveB2BFlag(
      contentStudioEnableEmailSaveBtn,
    ),
    contentStudioEnableShareableImageSave: resolveB2BFlag(
      contentStudioEnableShareableImageSaveBtn,
    ),
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    isEnterprisePlatform: enableB2B || false,
    isDatoUser,
    renderUsingChunks: true,
    enableCreatorStudioNav: resolveAdminFlag(enableCreatorStudio),
    storyManagerEnableAddStoriesButton: resolveAdminFlag(
      enableCreatorStudio,
      storyManagerEnableAddStories,
    ),
    hideStoryDashboardNav: hideStoryDashboardNav || false,
    enableBlogRegeneration: resolveB2BFlag(contentStudioEnableBlogRegeneration),
    enableEmailRegeneration: resolveB2BFlag(
      contentStudioEnableEmailRegenerationBtn,
    ),
    enableShareableImageRegeneration: resolveB2BFlag(
      contentStudioEnableShareableImageRegeneration,
    ),
    enableCaptionTemplatesTab: false,
    enableVideoClipEdit: resolveAdminFlag(contentStudioEnableClipEdit),
    enableTalkingPointsStudioSave: resolveB2BFlag(
      contentStudioEnableTalkingPointsSaveBtn,
    ),
    enableTalkingPointsStudioRegeneration: resolveB2BFlag(
      contentStudioEnableTalkingPointsRegenerationBtn,
    ),
    enableImportXml: resolveAdminFlag(enableImportXml),
    enableAiStoryProducer: resolveAdminFlag(enableAiStoryProducer),
    enableFixTranscriptButton: resolveAdminFlag(enableFixTranscriptButton),
    enableReframingModeIn16_9: enableReframingModeIn169,
    enableLargeFileSizeUpload,
    enableHeadlessEditor: true,
    enableHeadlessEditorExperimental: true,
    enableHeadlessEditorWave3: true,
    enableHeadlessEditorWave4: true,
    enableNoLimitFileSizeUpload,
    enableTimelineSnapGuide,
    enableTimelineSnapping,
    enableExperimentalTimelineFrameThumbnails,
    enableStackedLayouts,
    enableTranscriptEditor,
    enableMultiFileConcat,
    removeTinyFragmentsFromSource: removeTinyFragmentsFromSource || false,
    contentStudioEnableAIContentLanguageSelection:
      contentStudioEnableAiContentLanguageSelection,
    enableSpeakersIdentification,
    enableTranscriptProviderSelector: resolveAdminFlag(
      enableTranscriptProviderSelector,
    ),
    enableClipThemeGeneration,
    enableTranscriptSegmentArchitecture,
    enableUrlUpload: enableUrlUpload || false,
    enableZoomConnection: enableZoomConnection || false,
    enableTranscriptDualWrite:
      enableTranscriptDualWrite === false ? false : true,
    enableTranscriptMigrationSizeLimit:
      enableTranscriptMigrationSizeLimit ?? true,
    // New S3 resources upload paths
    // Enables dual write to S3 using signed urls and API upload
    enableVideoResourcesS3DualWrite: enableVideoResourcesS3DualWrite ?? false,
    // Enables dual write transcript snapshots to S3 and Dato API
    enableTranscriptSnapshotResourceDualWrite:
      enableTranscriptSnapshotResourceDualWrite ?? false,
    // Enables upload of transcript snapshots to S3 with VideoResourcesProvider
    enableTranscriptSnapshotResourceUpload:
      (enableTranscriptSnapshotResourceDualWrite ||
        enableTranscriptSnapshotResourceUpload) ??
      false,
    // Enables upload ofvideo source to S3 using signed urls
    enableVideoResourcesS3Upload:
      (enableVideoResourcesS3DualWrite || enableVideoResourcesS3Upload) ??
      false,
    enableParallelClipSave:
      (enableParallelClipSave && enableTranscriptSegmentArchitecture) ?? false,
    storyManagerEnableAwsSdkUploads: storyManagerEnableAwsSdkUploads ?? false,
    storyManagerEnableUnifiedUploadProgress:
      storyManagerEnableUnifiedUploadProgress || false,
    enableNewKaraokeArchitecture: enableNewKaraokeArchitecture || false,
    enableKaraokeDualWrite: enableKaraokeDualWrite ?? true,
    enableGenerateAiPhoto: enableGenerateAiPhoto || false,
  };
}
